import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {eye} from '../../../../assets/images/images';
import CardSummary from 'components/CardSummary';
import {
  DashboardLayout,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SearchInput,
  SecondaryButton,
  TableLayout,
} from 'components';
import {Constants} from 'helpers';
import {transformObject} from '../utils';
import {useQuery} from 'react-query';
import {fetchAccounts} from '../../../../requests/queries/flows';
import {PageContext} from '../../../../helpers/context';
import moment from 'moment/moment';
import PageNumber from 'components/PageNumber';
import {useSelector} from 'react-redux';
import FilterV2 from '../../../../components/FilterV2';
import {useFlowsLogic} from '../hooks/useFlows';

function FlowsAccountIndex() {
  const {
    userDetails: {company},
  } = useSelector(state => state?.auth);

  localStorage.removeItem('currentAccount');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('1');
  const [limit, setLimit] = useState(10);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    start: null,
    end: null,
  });

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('easydetect');
  }, [setPageName]);

  const params = useMemo(
    () =>
      transformObject({
        company_id: company?.id,
        limit,
        page: currentPage,
        ...filters,
      }),
    [company?.id, limit, currentPage, filters],
  );

  const {flows} = useFlowsLogic({isFilter: true});

  const projectFilters = flows?.flows?.map(flow => ({
    label: flow.name,
    value: flow.id,
  }));

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  const handleFilterChange = values => {
    const {dateRange, ...rest} = values;
    delete rest.date;

    setFilters({...rest, date_from: dateRange?.start, date_to: dateRange?.end});
    setCurrentPage(1);
  };

  const filterOptions = useMemo(
    () => [
      {
        key: 'project_id',
        label: 'Flows',
        type: 'select',
        multiSelect: false,
        options: projectFilters,
      },
    ],
    [projectFilters],
  );

  const {data: accountsData, isLoading} = useQuery(
    ['all-users-accounts', {params}],
    () => fetchAccounts({params}),
    {
      enabled: company?.id?.length > 0,
    },
  );

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const ACCOUNTS = useMemo(
    () => (!isLoading && accountsData && accountsData?.rows) || [],
    [accountsData, isLoading],
  );

  const handleRowClick = account => {
    navigate(
      Constants.PAGES_URL.FLOWS_ACCOUNTS_OVERVIEW.replace(
        ':slug',
        company?.id,
      ).replace(':id', account?.id),
      {
        state: {
          ...account,
          name: account?.user_name,
        },
      },
    );
    localStorage.setItem('currentAccount', JSON.stringify(account || {}));
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Fraud Flows</p>
      }
    >
      <div className="flex items-center justify-end py-2 flex-wrap gap-3 mx-[-32px] px-8 border border-transparent border-b-[#E4E3EC]">
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>
      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2 filters={filterOptions} onFilterChange={handleFilterChange} />
      </div>
      <div className="py-3">
        <CardSummary
          title="Total Unique Accounts"
          body={accountsData?.count || 0}
          className="!bg-white"
          loading={isLoading}
        />

        <div className="flex items-center justify-between my-3 gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <SearchInput
              onChange={value => {
                setFilters(prev => ({...prev, search: value, page: 1}));
              }}
            />
          </div>
        </div>

        <TableLayout negativeMargins>
          <thead className="font-semibold uppercase">
            <tr className="bg-white80">
              <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                Name
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                Email address
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                Account Number
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                Current Risk Level
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                Last activity date
              </th>
              <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap"></th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={8}>
                {isLoading ? (
                  <Loader height={65} />
                ) : !isLoading && ACCOUNTS?.length === 0 ? (
                  <EmptyState body="No account(s) found" noBtn height={65} />
                ) : null}
              </td>
            </tr>
          </tbody>

          <tbody className="text-sm bg-white">
            {ACCOUNTS &&
              ACCOUNTS?.map((account, i) => (
                <tr className="border-[0.5px] border-[#E4E3EC]" key={i}>
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-body whitespace-nowrap capitalize">
                    {account?.user_name || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {account?.user_email || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {account?.user_account_number || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {account?.user_risk_level || '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {moment(account?.createdAt).format('Do MMM YYYY hh:mm') ||
                      '-'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <button
                      className="h-4 aspect-square"
                      onClick={() => handleRowClick(account)}
                    >
                      <img src={eye} alt="" />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </TableLayout>

        {!isLoading && ACCOUNTS.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
            <PageNumber
              currentPage={currentPage}
              limit={limit}
              totalCount={accountsData?.count || 0}
            />

            <div className="flex items-center gap-5">
              <PageLimit
                onLimitChange={setLimit}
                total={accountsData.count}
                length={ACCOUNTS.length}
                limit={limit}
              />

              <Pagination
                total={Math.ceil(accountsData.count / limit)}
                current={+currentPage}
                onPageChange={activePage => {
                  localStorage.setItem('page', String(activePage));
                  pageClick(activePage);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default FlowsAccountIndex;
